import { useParams } from 'react-router-dom'
import {
    comparePrimitiveArrays,
    comparePrimitiveObjects,
    getUserDisplayName,
} from '../../../common/helpers'
import useAuth from '../../../common/hooks/useAuth'
import DataFormNew from '../../../components/common/DataFormNew'
import { useUploads } from '../../../common/UploadContext'
import { API_URL } from '../../../common/constants'
import useToast from '../../../common/hooks/useToast'
import SectionContainer from '../../../components/common/SectionContainer'

const permissions = {
    user: {
        admin: {
            fields: 'firstName,lastName,email,onBlackList,isBlocked,teamRoles,role',
        },
        assistant: {
            fields: 'firstName,lastName,email,onBlackList,isBlocked,teamRoles,role',
        },
        sales: {
            fields: 'firstName,lastName,email,role',
        },
        collections: {
            fields: 'firstName,lastName,email,role',
        },
        staff: {
            fields: 'firstName,lastName,email,role',
        },
    },
}

function getExtraHasChanged(data, item) {
    return (
        data.attachmentFiles?.length > 0 ||
        !comparePrimitiveObjects(
            data?.requirements || {},
            item?.submissionRequirements || {},
        )
    )
}

function EditTask() {
    const { taskId } = useParams()
    const auth = useAuth()
    const { uploadFiles } = useUploads()
    const setToast = useToast()

    let fields = {}
    if (auth.isAdmin) {
        fields = permissions.user.admin.fields
    } else if (auth.isAssistant) {
        fields = permissions.user.assistant.fields
    } else if (auth.isSales) {
        fields = permissions.user.sales.fields
    } else if (auth.isCollections) {
        fields = permissions.user.collections.fields
    } else if (auth.isStaff) {
        fields = permissions.user.staff.fields
    }

    const inputs = [
        {
            key: 'projectAndPhaseId',
            label: 'Project',
            type: 'liveSearch',
            getResultValue: (item) => {
                return item.title
            },
            getItemsFromResults: (data) => {
                const results = data?.results || []
                console.log('res', results)
                return results
                    .map((pr) => {
                        // console.log(pr)
                        return pr.phases?.length
                            ? pr.phases.map((ph) => ({
                                  id: `${pr.id}_${ph._id || ph.id}`,
                                  title: `${pr.title} / ${ph.title}`,
                              }))
                            : [
                                  {
                                      title: pr.title,
                                      id: pr.id,
                                  },
                              ]
                    })
                    .flat()
            },
            limit: 20,
            url: `/v1/tasks/projects?fields[]=title&fields[]=phases`,

            setDataOnChange: (_v, data) => {
                return {
                    ...data,
                    projectPhaseId: '',
                }
            },
        },

        {
            key: 'order',
            type: 'number',
            label: 'Priority #',
            id: 'form-order',
            autoComplete: 'new-password',
        },

        {
            key: 'userOrder',
            type: 'number',
            label: 'User priority #',
            id: 'form-user-order',
            autoComplete: 'new-password',
        },
        {
            key: 'title',
            label: 'Title',
            id: 'form-title',
            autoComplete: 'new-password',
        },
        {
            key: 'descriptionHtml',
            label: 'Description',
            type: 'html',
            id: 'form-description',
            onEditorChange: (v, setOptsState) => {
                setOptsState((prev) => ({ ...prev, descriptionEditor: v }))
            },
        },
        {
            key: 'descriptionSassCode',
            label: 'Sass',
            type: 'textarea',
            rows: 16,
            shouldHide: (_data, _item, optsState) => {
                return optsState.descriptionEditor !== 'code'
            },
        },
        {
            key: 'assignedTeamMemberId',
            label: 'Assigned team member',
            type: 'liveSearch',
            shouldHide: (data) => !['team', 'role'].includes(data.target),
            getResultValue: (item) => getUserDisplayName(item),
            getItemsFromResults: (data) => data?.results || [],
            limit: 20,
            url: `/v1/users?isBlocked=false&role=staff&fields=${fields}`,
        },
        {
            key: 'associatedClientType',
            label: 'Client type',

            type: 'switch',
            options: [
                {
                    value: 'User',
                    label: 'User',
                },
                {
                    value: 'Org',
                    label: 'Organization',
                },
            ],
        },
        {
            key: 'associatedUserClientId',
            label: 'User client',
            type: 'liveSearch',
            shouldHide: (data) => data.associatedClientType !== 'User',
            getResultValue: (item) => getUserDisplayName(item),
            getItemsFromResults: (data) => data?.results || [],
            limit: 20,
            url: `/v1/users?isBlocked=false&role=user&fields=firstName,lastName,email,role`,
        },
        {
            key: 'associatedOrgClientId',
            label: 'Organization client',
            type: 'liveSearch',
            shouldHide: (data) => data.associatedClientType !== 'Org',
            limit: 20,
            url: '/v1/organizations',
            getResultValue: (item) => item.name,
            getItemsFromResults: (data) => data?.results || [],
        },
        {
            key: 'tags',
            type: 'liveSearchAddItems',
            label: 'Tags (beta)',
            canAddNewItem: true,
            url: '/v1/tags?resource=Task',
            getItemText: (item) => item,
            fields: [
                {
                    column: 1,
                    getValue: (item) => item,
                },
            ],
            infoBottom: 'Tags are visible to all users.',
        },
        {
            key: 'priority',
            type: 'select',
            label: 'Priority',
            id: 'form-priority',
            options: [
                {
                    value: 'low',
                    label: 'Low',
                },
                {
                    value: 'medium',
                    label: 'Medium',
                },
                {
                    value: 'high',
                    label: 'High',
                },
            ],
        },
        {
            shouldHide: (data) =>
                !['role', 'team', 'client'].includes(data.target),
            key: 'requirements',
            type: 'select',
            label: 'Requirements',
            id: 'form-requirements',
            options: [
                {
                    value: 'none',
                    label: 'None',
                },
                {
                    value: 'text_link',
                    label: 'Text + Link',
                },
                {
                    value: 'text_image',
                    label: 'Text + Image',
                },
                {
                    value: 'text_file',
                    label: 'Text + File',
                },
            ],
        },
        // {
        //     shouldHide: (data) => !data.attachmentFileIds?.length,
        //     key: 'attachmentFileIds',
        //     type: 'driveFiles',
        //     label: 'Remove attachments',
        //     id: 'form-attachment-file-ids',
        // },
        {
            shouldHide: (data) =>
                !data.attachmentFileUrls?.length ||
                !['role', 'team', 'client'].includes(data.target),
            key: 'attachmentFileUrls',
            type: 'removeFiles',
            label: 'Remove attachments',
            id: 'form-attachment-file-urls',
        },
        {
            shouldHide: (data) =>
                !['role', 'team', 'client'].includes(data.target),
            key: 'attachmentFiles',
            type: 'file',
            label: 'Upload attachments',
            id: 'form-attachment-browser',
            multiple: true,
            infoBottom: 'Add attachments from device.',
        },
        {
            key: 'dueDate',
            type: 'date',
            label: 'Due date',
            id: 'form-due-date',
        },
        {
            key: 'timeLimitHours',
            type: 'number',
            label: 'Time limit hours',
            id: 'form-time-limit-hours',
            inlineGroup: 'Time limit',
        },
        {
            key: 'timeLimitMinutes',
            type: 'number',
            label: 'Time limit minutes',
            id: 'form-time-limit-minutes',
            inlineGroup: 'Time limit',
        },
        {
            key: 'target',
            type: 'hidden',
        },
    ]

    function mapItemToData(item) {
        const newData = {
            title: item.title,
            descriptionHtml: item.descriptionHtml,
            descriptionSassCode: item.descriptionSassCode,
            priority: item.priority,
            tags: item.tags || [],
            attachmentFileIds: item.attachmentFileIds,
            attachmentFileUrls: item.attachmentFileUrls,
            order: String(item.order),
            userOrder: String(item.userOrder),
            target: item.target,
            dueDate: item.dueDate ? item.dueDate.split('T')[0] : null,
            timeLimitHours: item.timeLimitMinutes
                ? Math.floor(item.timeLimitMinutes / 60)
                : null,
            timeLimitMinutes: item.timeLimitMinutes
                ? item.timeLimitMinutes % 60
                : null,
        }
        if (item.submissionRequirements) {
            const { text, link, imageFileId, otherFileId } =
                item.submissionRequirements
            if (!text && !link && !imageFileId && !otherFileId) {
                newData.requirements = 'none'
            } else if (text && link) {
                newData.requirements = 'text_link'
            } else if (text && imageFileId) {
                newData.requirements = 'text_image'
            } else if (text && otherFileId) {
                newData.requirements = 'text_file'
            }
        } else {
            newData.requirements = 'none'
        }

        if (
            ['team', 'role'].includes(item.target) &&
            item.assignedTeamMemberId
        ) {
            newData.assignedTeamMemberId = getUserDisplayName(
                item.assignedTeamMember,
            )
        }

        if (item.associatedClientId) {
            if (item.associatedClientType === 'User') {
                newData.associatedUserClientId = getUserDisplayName(
                    item.associatedUserClient,
                )
            } else if (item.associatedClientType === 'Org') {
                newData.associatedOrgClientId =
                    item.associatedOrgClient?.name || 'Unknown'
            }
            newData.associatedClientType = item.associatedClientType
        }

        if (item.project) {
            newData.projectAndPhaseId = item.project.title
            if (item.projectPhaseId) {
                newData.projectAndPhaseId += ` / ${item.projectPhase.title}`
            }
        }

        return newData
    }

    async function getBody(data, item) {
        const MAX_FILE_SIZE = 50 * 1024 * 1024

        for (const file of data.attachmentFiles || []) {
            if (file.size > MAX_FILE_SIZE) {
                throw new Error(`Max file size is 50MB.`)
            }
        }

        const body = {
            title: item.title !== data.title ? data.title : undefined,
            descriptionHtml:
                item.descriptionHtml !== data.descriptionHtml
                    ? data.descriptionHtml
                    : undefined,
            descriptionSassCode:
                item.descriptionSassCode !== data.descriptionSassCode
                    ? data.descriptionSassCode
                    : undefined,
            priority:
                item.priority !== data.priority ? data.priority : undefined,
            order:
                Number(data.order) !== Number(item.order)
                    ? Number(data.order)
                    : undefined,
            userOrder:
                Number(data.userOrder) !== Number(item.userOrder)
                    ? Number(data.userOrder)
                    : undefined,
            tags: !comparePrimitiveArrays(item.tags, data.tags)
                ? data.tags
                : undefined,
            attachmentFileIds: !comparePrimitiveArrays(
                item.attachmentFileIds,
                data.attachmentFileIds,
            )
                ? data.attachmentFileIds
                : undefined,
            dueDate: data.dueDate
                ? new Date(data.dueDate).toISOString().split('T')[0]
                : undefined,
            timeLimitMinutes:
                data.timeLimitMinutes !== undefined ||
                data.timeLimitHours !== undefined
                    ? Number(data.timeLimitHours || 0) * 60 +
                      Number(data.timeLimitMinutes || 0)
                    : undefined,
        }

        let newSubmissionRequirements = {}
        if (data.requirements === 'text_link') {
            newSubmissionRequirements = {
                text: true,
                link: true,
                imageFileId: false,
                otherFileId: false,
            }
        } else if (data.requirements === 'text_image') {
            newSubmissionRequirements = {
                text: true,
                imageFileId: true,
                otherFileId: false,
                link: false,
            }
        } else if (data.requirements === 'text_file') {
            newSubmissionRequirements = {
                text: true,
                otherFileId: true,
                imageFileId: false,
                link: false,
            }
        }

        if (
            !comparePrimitiveObjects(
                item.submissionRequirements,
                newSubmissionRequirements,
            )
        ) {
            body.submissionRequirements = newSubmissionRequirements
        }

        if (data.assignedTeamMemberId !== item.assignedTeamMemberId) {
            body.assignedTeamMemberId = data.assignedTeamMemberId
        }

        // TODO could be duplicates
        if (
            data.assignedTeamMemberId ===
            getUserDisplayName(item.assignedTeamMember)
        ) {
            delete body.assignedTeamMemberId
        }

        if (
            data.associatedUserClientId !== item.associatedClientId ||
            data.associatedOrgClientId !== item.associatedClientId ||
            data.associatedClientType !== item.associatedClientType
        ) {
            body.associatedClientId =
                data.associatedClientType === 'User'
                    ? data.associatedUserClientId || null
                    : data.associatedOrgClientId || null
            body.associatedClientType = body.associatedClientId
                ? data.associatedClientType || null
                : null
        }

        // TODO could be duplicates
        if (
            data.associatedUserClientId ===
                getUserDisplayName(item.associatedUserClient) &&
            data.associatedClientType === 'User'
        ) {
            delete body.associatedClientId
            delete body.associatedClientType
        }

        if (
            data.associatedOrgClientId === item.associatedOrgClient?.name &&
            data.associatedClientType === 'Org'
        ) {
            delete body.associatedClientId
            delete body.associatedClientType
        }

        // TODO could be duplicates
        if (!data.projectAndPhaseId?.startsWith(item.project?.title)) {
            const dataProjectId = data.projectAndPhaseId?.split('_')?.[0]
            const dataPhaseId = data.projectAndPhaseId?.split('_')?.[1]
            body.projectId = dataProjectId || null
            body.projectPhaseId = dataPhaseId || null
        }
        delete body.projectAndPhaseId

        if (
            data.attachmentFileUrls?.length !== item.attachmentFileUrls?.length
        ) {
            const removedFiles = item.attachmentFileUrls.filter(
                (url) => !data.attachmentFileUrls.includes(url),
            )
            if (removedFiles.length) {
                body.attachmentFileUrlsRemove = removedFiles
            }
        }

        const formData = new FormData()
        const bodyData = { ...body }

        formData.append('data', JSON.stringify(bodyData))

        if (data.attachmentFiles?.length) {
            const { error } = uploadFiles(data.attachmentFiles, {
                patchUrl: `${API_URL}/v1/tasks/${taskId}`,
                patchField: 'attachmentFileUrlsAdd',
            })
            if (error) {
                setToast(error, 'alert')
            } else {
                setToast('Files upload in progress, it may take a few minutes.')
            }
        }

        return formData
    }

    return (
        <SectionContainer>
            <DataFormNew
                url={`/v1/tasks/${taskId}`}
                method="PATCH"
                submitText="Save"
                submitToast="Saved"
                submitNavArg={`/tasks/${taskId}`}
                inputs={inputs}
                getBody={getBody}
                mapItemToData={mapItemToData}
                mainButton
                mutationRegexes={[/\/v1\/tasks/]}
                getExtraHasChanged={getExtraHasChanged}
                hasHistory
            />
        </SectionContainer>
    )
}

export default EditTask
